import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, Subject } from 'rxjs';
import { filter, startWith, takeUntil } from 'rxjs/operators';

import { AuthorizationType } from '@shared/models/AuthorizationType';
import { getUserInfo, isAuthenticated } from '@shared/store/selectors/security.selector';

import { pendingSqaRunKey } from './sqa-run-mode';

// Where an SQA run is loaded (TSP-E-157-22): the Playground reads the pending key on init.
export const SQA_RUN_PLAYGROUND_URL = '/playground/xact';

// A guard that keeps sending the app somewhere else must not become a navigation loop: past this many tries the
// app stays where it was put and the key stays pending for the next visit to the Playground.
const MAX_REDIRECTS = 5;

/**
 * TAKES AN SQA SESSION TO THE PLAYGROUND (TSP-E-157-22). The company may open somewhere else by default —
 * HomeRedirectCommand, or an Intention that runs after "Continue to Orbitax" — so this does not send the app
 * once and hope: while a run key is pending and the user is signed in to a company, every navigation that ends
 * anywhere but the Playground is followed by one to it. The Playground takes the key once the run is under way
 * (or refused), and from then on this service does nothing, so the run's own navigations are never pulled back.
 */
@Injectable({ providedIn: 'root' })
export class SqaRunRoutingService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private started = false;
  private redirects = 0;

  constructor(private readonly router: Router, private readonly securityStore: Store<any>) {}

  start(): void {
    if (this.started || !pendingSqaRunKey()) return;
    this.started = true;

    const navigated$ = this.router.events.pipe(filter(event => event instanceof NavigationEnd), startWith(null));
    combineLatest([
      this.securityStore.select(isAuthenticated),
      this.securityStore.select(getUserInfo),
      navigated$
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([auth, userInfo]) => {
        if (auth !== AuthorizationType.AUTHORIZED || !userInfo?.companyId) return;
        if (!pendingSqaRunKey()) {
          this.destroy$.next();
          return;
        }
        if (this.router.url.split('?')[0].startsWith(SQA_RUN_PLAYGROUND_URL)) return;
        if (this.redirects >= MAX_REDIRECTS) return;

        this.redirects++;
        this.router.navigateByUrl(SQA_RUN_PLAYGROUND_URL);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
